import React, { useCallback, useEffect, useRef, useState } from "react"
import LOGO_BLIK from "assets/images/payment_method_blik.svg"
import LOGO_CARD from "assets/images/payment_method_card.png"
import LOGO_TRANSFER from "assets/images/payment_method_transfer.png"
import { AddOfferHelperText, ChangeLanguageComponent } from "components/shared/ModalHelpers"
import { useCustomQuery } from "hooks/useCustomQuery"
import { useForm } from "react-hook-form"
import { Trans, useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useNavigate, useParams } from "react-router-dom"
import { AnalyticsService } from "services/AnalyticsService"
import { ProfileOfferService, ToolsService } from "services/api-service"
import { SentryService } from "services/SentryService"
import { RootState } from "store/store"
import styled from "styled-components"
import { Box } from "ui/components/atoms/_atoms"
import Button from "ui/components/atoms/button"
import TextCheckbox from "ui/components/atoms/text-checkbox"
import { LabelH3 } from "ui/components/atoms/typography"
import Accordion from "ui/components/molecules/accordion"
import Modal from "ui/components/molecules/modal"
import SelectRow from "ui/components/molecules/select-row"
import PricingSection from "ui/components/sections/pricing-section"
import { PLANS_ARRAY } from "ui/data"

const ExtendOffer = () => {
  const { t, i18n } = useTranslation(["add-offer", "common"])
  const { id = "" } = useParams()
  const navigate = useNavigate()

  const { data: transactionsNotices } = useCustomQuery<[{ title: string; content: string }]>(
    { key: ["transaction-notice", i18n.language] },
    () => ToolsService.getTransactionsNotices(i18n.language),
  )

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    reset,
    formState: { errors, isValid },
  } = useForm()
  const { profile } = useSelector((state: RootState) => state.profile)

  const defaultCountryCode =
    profile?.type === "company" ? profile?.company?.countryCode : profile?.address?.countryCode
  const isPoland = defaultCountryCode === "PL"

  const [loading, setLoading] = useState(false)

  const [params, setParams] = useState<{
    code?: string
    type?: string
    period: string
    plan: string
  }>({
    period: "1",
    type: "offer",
    plan: PLANS_ARRAY["offer"][0].slug,
  })
  const [paymentMethod, setPaymentMethod] = useState(isPoland ? "transfer" : "card")

  const checkbox1 = watch("checkbox1")
  const checkbox2 = watch("checkbox2")

  const handleChangeParams = useCallback(({ code, type, period, plan }: any) => {
    setParams({
      code,
      type,
      period,
      plan,
    })
  }, [])

  const onSubmit = useCallback(async () => {
    try {
      setLoading(true)
      const res = await ProfileOfferService.extendOffer(id, {
        plan: params?.plan,
        period: `period_${params.period}`,
        currency: i18n.language === "pl" ? "PLN" : "EUR",
        paymentMethod: paymentMethod,
      })
      AnalyticsService.track("panel_offer_extend_success", {
        plan: params?.plan,
        period: params.period,
        currency: i18n.language === "pl" ? "PLN" : "EUR",
        paymentMethod: paymentMethod,
      })
      navigate(`/transaction/status/${res.offerId}/${res.transactionId}`)
    } catch (e) {
      window.alert("Nie udało się przedłużyć ogłoszenia! Proszę spróbować później.")
      SentryService.error("[ERROR ProfileOfferService.extendOffer]:", JSON.stringify(e))
    } finally {
      setLoading(false)
    }
  }, [i18n.language, id, navigate, params.period, params?.plan, paymentMethod])

  useEffect(() => {
    AnalyticsService.track("panel_offer_extend_open")
  }, [])

  return (
    <Modal
      show={true}
      icon="clock"
      title={t("extend-offer.title")}
      text={t("extend-offer.text")}
      setShow={() => navigate(-1)}
      $maxWidth="700px"
      showBackground={false}
      mobileFullScreen={true}
      helperElement={
        <ChangeLanguageComponent>
          <AddOfferHelperText />
        </ChangeLanguageComponent>
      }
      bottomElement={
        <Button $width="100%" loading={loading} onClick={handleSubmit(onSubmit)}>
          {t("summary.submit")}
        </Button>
      }
    >
      <>
        <PricingSection
          initialCode={undefined}
          fullSize={false}
          showDiscount={false}
          setParams={handleChangeParams}
          currency={i18n.language === "pl" ? "pl" : "eur"}
          translator={e => t(`basket.${e}`, { ns: "add-offer" })}
          defaultPlan={0}
          $marginBottom="M"
        />
        <Accordion $marginBottom="M" title={t("summary.payment.title")}>
          <PaymentMethodList>
            {isPoland && (
              <>
                <SelectRow
                  checked={paymentMethod === "transfer"}
                  label={<LabelH3 $marginBottom="0">{t("summary.payment.fast-transfer")}</LabelH3>}
                  value={<img height={25} src={LOGO_TRANSFER} style={{ display: "block" }} />}
                  onClick={() => setPaymentMethod("transfer")}
                />
                <SelectRow
                  checked={paymentMethod === "blik"}
                  label={<LabelH3 $marginBottom="0">{t("summary.payment.blik")}</LabelH3>}
                  value={
                    <TransferRow>
                      <img height={25} src={LOGO_BLIK} style={{ display: "block" }} />
                    </TransferRow>
                  }
                  onClick={() => setPaymentMethod("blik")}
                />
              </>
            )}
            <SelectRow
              checked={paymentMethod === "card"}
              label={<LabelH3 $marginBottom="0">{t("summary.payment.card")}</LabelH3>}
              value={<img height={25} src={LOGO_CARD} style={{ display: "block" }} />}
              onClick={() => setPaymentMethod("card")}
            />
            <SelectRow
              checked={paymentMethod === "pro_forma"}
              label={<LabelH3 $marginBottom="0">{t("summary.payment.pro-forma")}</LabelH3>}
              onClick={() => setPaymentMethod("pro_forma")}
            />
          </PaymentMethodList>
        </Accordion>
        <Box $marginTop="M" $marginBottom="M">
          <TextCheckbox
            name="checkbox1"
            value={checkbox1}
            error={errors.checkbox1}
            rhf={register("checkbox1", {
              required: true,
            })}
            $marginBottom="S"
          >
            *
            <Trans
              i18nKey={t("legal.accept-terms", { ns: "form" })}
              components={{
                terms: <a target="_blank" rel="noreferrer" href="https://jobintrans.eu/pl/terms" />,
                privacy: (
                  <a target="_blank" rel="noreferrer" href="https://jobintrans.eu/pl/privacy" />
                ),
              }}
            />
          </TextCheckbox>
          <TextCheckbox
            name="checkbox2"
            value={checkbox2}
            error={errors.checkbox2}
            rhf={register("checkbox2", {
              required: true,
            })}
            $marginBottom="S"
          >
            <div
              dangerouslySetInnerHTML={{ __html: `*${t("legal.accept-payment", { ns: "form" })}` }}
            />
          </TextCheckbox>
          {(paymentMethod === "card" || paymentMethod === "blik") &&
            transactionsNotices &&
            transactionsNotices?.map((item, index) => (
              <SafeHtml key={index} html={`${item.title} - ${item.content}`} />
            ))}
        </Box>
      </>
    </Modal>
  )
}

export default ExtendOffer

const TransferRow = styled(Box)`
  display: flex;
  grid-gap: ${({ theme }) => theme.font.S};
  align-items: center;
`

const TransferNoticeP = styled(Box)`
  font-size: ${({ theme }) => theme.font.XS};
  color: ${({ theme }) => theme.color.secondary};
  font-weight: ${({ theme }) => theme.font.light};

  a,
  span {
    color: ${({ theme }) => theme.color.primary};
  }
`

const SafeHtml = ({ html }: { html: string }) => {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (ref.current) {
      ref.current.innerHTML = html
    }
  }, [html])
  return <TransferNoticeP $marginBottom="S" ref={ref} />
}

const PaymentMethodList = styled(Box)`
  display: flex;
  flex-direction: column;
  grid-gap: ${({ theme }) => theme.space.XS};
`

const InvoiceWrapper = styled(Box)`
  display: grid;
  grid-template-columns: auto max-content max-content max-content;
  grid-gap: ${({ theme }) => theme.space.M};

  @media (max-width: 600px) {
    grid-template-columns: 1fr 1fr 1fr;

    > *:first-child {
      grid-column-start: 1;
      grid-column-end: 4;
    }

    > *:nth-child(5) {
      grid-column-start: 2;
      grid-column-end: 3;
    }
  }
`

const PersonalWrapper = styled(Box)`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-gap: ${({ theme }) => theme.space.S};

  @media (max-width: 800px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`

const CompanyWrapper = styled(Box)`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  grid-gap: ${({ theme }) => theme.space.S};

  @media (max-width: 800px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`

const TagsWrapper = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  grid-column-gap: ${({ theme }) => theme.space.M};
  grid-row-gap: ${({ theme }) => theme.space.S};
`
